import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import { firebaseApp } from '../firebase';

export interface SubscriptionData {
  subscriptionPlan?: string;
  planDuration?: number;
  startDate?: string;
  endDate?: string;
  isActive?: boolean;
  paymentId?: string;
  orderId?: string;
  amountPaid?: number;
  lastPaymentDate?: string;
}

export interface SubscriptionStatus {
  hasSubscription: boolean;
  isActive: boolean;
  isExpired: boolean;
  planName?: string;
  endDate?: Date;
  daysRemaining?: number;
}

const db = getFirestore(firebaseApp);

/**
 * Check if an HR user has an active subscription
 * Supports both old format (subscriptionPlan, endDate) and new format (subscription object)
 */
export const checkHRSubscription = async (userId: string): Promise<SubscriptionStatus> => {
  try {
    const recruiterDoc = await getDoc(doc(db, 'recruiters', userId));

    if (!recruiterDoc.exists()) {
      return {
        hasSubscription: false,
        isActive: false,
        isExpired: false,
      };
    }

    const data = recruiterDoc.data();

    // Check NEW subscription object format first
    if (data.subscription) {
      const sub = data.subscription;

      // If plan is 'none' or status is 'inactive', no subscription
      if (sub.plan === 'none' || sub.status === 'inactive') {
        return {
          hasSubscription: false,
          isActive: false,
          isExpired: false,
          planName: sub.plan,
        };
      }

      // Check expiry if expiresAt is set
      if (sub.expiresAt) {
        const expiryDate = sub.expiresAt.toDate ? sub.expiresAt.toDate() : new Date(sub.expiresAt);
        const now = new Date();
        const isExpired = expiryDate < now;
        const daysRemaining = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        return {
          hasSubscription: true,
          isActive: !isExpired && sub.status === 'active',
          isExpired,
          planName: sub.plan,
          endDate: expiryDate,
          daysRemaining: daysRemaining > 0 ? daysRemaining : 0,
        };
      }

      // No expiry date set, check status only
      return {
        hasSubscription: sub.status === 'active',
        isActive: sub.status === 'active',
        isExpired: false,
        planName: sub.plan,
      };
    }

    // Fallback to OLD format (subscriptionPlan, endDate)
    const oldData = data as SubscriptionData;

    // Check if subscription data exists
    if (!oldData.subscriptionPlan || !oldData.endDate) {
      return {
        hasSubscription: false,
        isActive: false,
        isExpired: false,
      };
    }

    // Check if subscription is expired
    const endDate = new Date(oldData.endDate);
    const now = new Date();
    const isExpired = endDate < now;

    // Calculate days remaining
    const daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    // If expired, update isActive to false
    if (isExpired && oldData.isActive) {
      await updateDoc(doc(db, 'recruiters', userId), {
        isActive: false,
        updatedAt: new Date().toISOString(),
      });
    }

    return {
      hasSubscription: true,
      isActive: !isExpired && oldData.isActive === true,
      isExpired,
      planName: oldData.subscriptionPlan,
      endDate,
      daysRemaining: daysRemaining > 0 ? daysRemaining : 0,
    };
  } catch (error) {
    return {
      hasSubscription: false,
      isActive: false,
      isExpired: false,
    };
  }
};

/**
 * Get subscription details for an HR user
 */
export const getSubscriptionDetails = async (userId: string): Promise<SubscriptionData | null> => {
  try {
    const recruiterDoc = await getDoc(doc(db, 'recruiters', userId));

    if (!recruiterDoc.exists()) {
      return null;
    }

    const data = recruiterDoc.data() as SubscriptionData;
    return data;
  } catch (error) {
    return null;
  }
};

/**
 * Check if subscription is about to expire (within 7 days)
 */
export const isSubscriptionExpiringSoon = (endDate: string): boolean => {
  const end = new Date(endDate);
  const now = new Date();
  const daysRemaining = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return daysRemaining <= 7 && daysRemaining > 0;
};
